import {Checkbox, CurrencyInput, Dialog, isCurrency, Separator} from 'platform/components';
import {Box, Grid, Show, Text, VStack} from 'platform/foundation';

import {Fragment, useState} from 'react';

import {
  usePatchBusinessCaseVehicleToBuyPricingMutation,
  type BuyingOfferPurchaseVehicleResponseBody,
  type BusinessCasePriceRequestBody,
} from '@dms/api/sales';
import {VatTypeEnum, type Money} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import type {AuctionMoney} from '../../model/auctionView.types';
import {useAuctionFormat} from './useAuctionFormat';

interface ConfirmMaxPriceDialogProps {
  isOpen: boolean;
  purchaseCaseId: string;
  vehicleId: string;
  offer: AuctionMoney;
  buying: BuyingOfferPurchaseVehicleResponseBody | null;
  onClose: () => void;
}

// shortcut: a VAT-deductible price is sent as a gross price, the backend derives the net price. Verify on a deductible case.
const toPurchasePrice = (
  money: Money,
  buying: BuyingOfferPurchaseVehicleResponseBody | null
): BusinessCasePriceRequestBody =>
  buying?.vatDeductible
    ? {
        priceNotDeductible: null,
        priceWithoutVat: null,
        priceWithVat: money,
        vatDirection: 'FROM_PRICE_WITH_VAT',
        vatType: buying.vatType,
      }
    : {
        priceNotDeductible: money,
        priceWithoutVat: null,
        priceWithVat: null,
        vatDirection: null,
        vatType: VatTypeEnum.Z,
      };

// Writes the same fields as the purchase case Pricing form (maxPurchasePrice, purchasePrice).
export function ConfirmMaxPriceDialog(props: ConfirmMaxPriceDialogProps) {
  const format = useAuctionFormat();
  const currency = props.offer.currency;
  const offerAmount = Number(props.offer.amount);
  const savedMax = props.buying?.maxBuyingPrice;
  const [retained, setRetained] = useState<number | null>(
    savedMax && savedMax.currency === currency ? offerAmount - Number(savedMax.amount) : 0
  );
  const [isCustomerPriceSet, setIsCustomerPriceSet] = useState(false);
  const [patchPricing, {isLoading}] = usePatchBusinessCaseVehicleToBuyPricingMutation();

  const maxPrice: Money | null =
    retained !== null && retained >= 0 && retained < offerAmount
      ? {amount: (offerAmount - retained).toFixed(2), currency}
      : null;
  const buyingPrice = props.buying?.buyingPrice;
  const currentPrice = buyingPrice?.priceNotDeductible ?? buyingPrice?.priceWithVat ?? null;
  const taxNote =
    props.buying?.vatDeductible === false
      ? i18n.t('entity.auction.confirmation.vatNotDeductible')
      : i18n.t('general.labels.withVat');

  const rows = [
    {label: i18n.t('entity.auction.confirmation.dealerOffer'), value: props.offer, testId: 'offer'},
    {label: i18n.t('entity.businessCase.labels.maxBuyingPrice'), value: maxPrice, testId: 'max'},
    {
      label: i18n.t('entity.auction.confirmation.currentBuyingPrice'),
      value: currentPrice,
      testId: 'current',
    },
  ];

  const save = async () => {
    if (maxPrice === null) {
      return;
    }
    await patchPricing({
      businessCaseId: props.purchaseCaseId,
      vehicleId: props.vehicleId,
      patchBusinessCaseVehicleToBuyPricingRequestBody: {
        maxPurchasePrice: maxPrice,
        ...(isCustomerPriceSet ? {purchasePrice: toPurchasePrice(maxPrice, props.buying)} : {}),
      },
    })
      .unwrap()
      .then(props.onClose)
      .catch(handleApiError);
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      size="small"
      title={i18n.t('entity.auction.confirmation.confirmMaxBuyingPrice')}
      onClose={props.onClose}
      buttons={[
        {
          title: i18n.t('entity.auction.confirmation.confirmMaxBuyingPrice'),
          variant: 'primary',
          isDisabled: maxPrice === null,
          isLoading,
          onClick: () => void save(),
          'data-testid': 'auction-confirm-max-price-save',
        },
        {title: i18n.t('general.actions.close'), variant: 'secondary', onClick: props.onClose},
      ]}
      data-testid="auction-confirm-max-price-dialog"
    >
      <VStack spacing={4}>
        <Text size="small" color="secondary">
          {i18n.t('entity.auction.confirmation.confirmMaxPriceNote')}
        </Text>
        <VStack>
          {rows.map((row, index) => (
            <Fragment key={row.testId}>
              <Show when={index > 0}>
                <Separator spacing={0} />
              </Show>
              <Grid
                columns={[1, 2]}
                spacing={4}
                data-testid={`auction-confirm-max-price-${row.testId}`}
              >
                <Box paddingVertical={3}>
                  <Text size="small" color="secondary">
                    {row.label}
                  </Text>
                </Box>
                <Box paddingVertical={3}>
                  <Text alternative>{row.value ? format.money(row.value) : '—'}</Text>
                  <Show when={row.value}>
                    <Text size="small" color="secondary">
                      {taxNote}
                    </Text>
                  </Show>
                </Box>
              </Grid>
            </Fragment>
          ))}
          <Separator spacing={0} />
        </VStack>
        <CurrencyInput
          label={i18n.t('entity.auction.confirmation.retainedAmount', {currency})}
          isRequired
          currency={isCurrency(currency) ? currency : null}
          value={retained}
          onChange={setRetained}
          isInvalid={retained !== null && maxPrice === null}
          errorMessage={
            retained !== null && retained >= offerAmount
              ? i18n.t('entity.auction.confirmation.retainedTooHigh', {
                  defaultValue: 'The retained amount must be lower than the offer.',
                })
              : null
          }
          data-testid="auction-confirm-max-price-retained"
        />
        <Separator spacing={0} />
        <Checkbox
          label={i18n.t('entity.auction.confirmation.setCustomerOfferPrice')}
          helperText={i18n.t('entity.auction.confirmation.setCustomerOfferPriceNote')}
          value={isCustomerPriceSet}
          onChange={setIsCustomerPriceSet}
          data-testid="auction-confirm-max-price-set-customer-price"
        />
      </VStack>
    </Dialog>
  );
}
